import { cloneDeep } from 'lodash-es';
import * as C from '../constants';
import itineraryService from '../../../services/itinerary-service';
import twentyService from '../../../services/twenty-service';
import filesService from '../../../services/files-service';
import router from '../../../router';

export default {
  /**
   * @param commit
   */
  clearState({ commit }) {
    commit(C.ITINERARY_CLEAR_STATE);
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  getProductTypes({ commit }) {
    commit(C.ITINERARY_PRODUCT_TYPES_PENDING);
    return itineraryService.getProductTypes()
      .then((res) => {
        commit(C.ITINERARY_PRODUCT_TYPES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_PRODUCT_TYPES_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getItineraries({ commit }) {
    commit(C.ITINERARY_OVERVIEW_PENDING);
    return itineraryService.getItineraries()
      .then((res) => {
        commit(C.ITINERARY_OVERVIEW_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_OVERVIEW_FAILURE);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  getItinerary({ commit }, payload) {
    commit(C.ITINERARY_LOAD_GENERAL_PENDING);
    return itineraryService.getItinerary(payload)
      .then((res) => {
        commit(C.ITINERARY_LOAD_GENERAL_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_LOAD_GENERAL_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  createItinerary({ commit, dispatch }, payload) {
    commit(C.ITINERARY_CREATE_PENDING);
    return itineraryService.createItinerary(payload)
      .then((res) => {
        commit(C.ITINERARY_CREATE_SUCCESS, { ...payload, id: res.data.id });
        router.push({ name: 'ItineraryEdit', params: { id: res.data.id } });
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_create_success',
        }, { root: true });
      }).catch((err) => {
        const errorCode = err.response.data.data;
        let errorMessageIdentifier = '';
        if (errorCode === 'itinerary_duplicate_violation') {
          errorMessageIdentifier = 'itinerary.errors.saveDuplicateKeyViolation';
        } else {
          errorMessageIdentifier = 'itinerary.errors.saveGeneric';
        }
        commit(C.ITINERARY_CREATE_FAILURE, errorMessageIdentifier);
      });
  },

  /**
   * @param commit
   * @param productTypeId
   * @returns {Promise<T | never>}
   */
  getCountriesByProductTypeId({ commit }, productTypeId) {
    commit(C.ITINERARY_MASTER_COUNTRIES_PENDING);
    return itineraryService.getCountriesByProductTypeId(productTypeId)
      .then((res) => {
        commit(C.ITINERARY_MASTER_COUNTRIES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_MASTER_COUNTRIES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param productTypeId
   * @returns {Promise<T>}
   */
  getCountriesWithAreasByProductTypeId({ commit }, productTypeId) {
    commit(C.ITINERARY_MASTER_COUNTRIES_PENDING);
    return itineraryService.getCountriesWithAreasByProductTypeId(productTypeId)
      .then((res) => {
        commit(C.ITINERARY_MASTER_COUNTRIES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_MASTER_COUNTRIES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {*|undefined}
   */
  updateItinerary({ commit, dispatch }, payload) {
    commit(C.ITINERARY_EDIT_PENDING);
    return itineraryService.updateItinerary(payload)
      .then(() => {
        commit(C.ITINERARY_EDIT_SUCCESS, payload);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_update_success',
        }, { root: true });
      }).catch((err) => {
        commit(C.ITINERARY_EDIT_FAILURE, err.response.data);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: err.response.data.data,
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T | never>}
   */
  getItinerarySalesCountries({ commit }, itineraryId) {
    commit(C.ITINERARY_SALES_COUNTRIES_PENDING);
    return itineraryService.getItinerarySalesCountries(itineraryId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_SALES_COUNTRIES_SUCCESS, res.data);
        } else {
          commit(C.ITINERARY_SALES_COUNTRIES_SUCCESS, []);
        }
      }).catch((err) => {
        commit(C.ITINERARY_SALES_COUNTRIES_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param countryIds
   */
  updateItinerarySalesCountries({ commit, dispatch }, { itineraryId, countryIds }) {
    commit(C.ITINERARY_SALES_COUNTRIES_UPDATE_PENDING);
    return itineraryService.updateItinerarySalesCountries(itineraryId, countryIds)
      .then(() => {
        commit(C.ITINERARY_SALES_COUNTRIES_UPDATE_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_update_sales_countries_success',
        }, { root: true });
        dispatch('getItinerarySalesCountries', itineraryId);
      }).catch((err) => {
        commit(C.ITINERARY_SALES_COUNTRIES_UPDATE_FAILURE, err.response.data);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: err.response.data.data,
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  getElcanoTripThemes({ commit }) {
    commit(C.ITINERARY_TRIP_THEMES_PENDING);
    return twentyService.getElcanoTripThemes()
      .then((res) => {
        commit(C.ITINERARY_TRIP_THEMES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_TRIP_THEMES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T>}
   */
  getAssignedTripThemes({ commit }, itineraryId) {
    commit(C.ITINERARY_TRIP_THEMES_ASSIGNED_PENDING);
    return itineraryService.getItineraryTripThemes(itineraryId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_TRIP_THEMES_ASSIGNED_SUCCESS, res.data);
        } else {
          commit(C.ITINERARY_TRIP_THEMES_ASSIGNED_SUCCESS, []);
        }
      }).catch((err) => {
        commit(C.ITINERARY_TRIP_THEMES_ASSIGNED_FAILURE, err.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param contentIds
   * @returns {Promise<T>}
   */
  updateAssignedTripThemes({ commit, dispatch }, { itineraryId, contentIds }) {
    commit(C.ITINERARY_TRIP_THEMES_UPDATE_PENDING);
    return itineraryService.updateItineraryTripThemes(itineraryId, contentIds)
      .then(() => {
        commit(C.ITINERARY_TRIP_THEMES_UPDATE_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_update_trip_themes_success',
        }, { root: true });
        dispatch('getAssignedTripThemes', itineraryId);
      }).catch(() => {
        commit(C.ITINERARY_TRIP_THEMES_UPDATE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_update_trip_themes_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T>}
   */
  getDayProgram({ commit }, itineraryId) {
    commit(C.ITINERARY_DAY_PROGRAM_PENDING);
    return itineraryService.getDayProgram(itineraryId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_DAY_PROGRAM_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.ITINERARY_DAY_PROGRAM_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_FAILURE);
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @returns {Promise<T>}
   */
  addDayProgramEntry({ commit, state, dispatch }, itineraryId) {
    commit(C.ITINERARY_DAY_PROGRAM_ADD_PENDING);
    const dayProgram = cloneDeep(state.dayProgram.items);
    return itineraryService.addDayProgramEntry(itineraryId)
      .then((res) => {
        const entry = Object.assign({});
        entry.description = '';
        entry.id = res.data.id;
        dayProgram.push(entry);
        commit(C.ITINERARY_DAY_PROGRAM_ADD_SUCCESS, dayProgram);
        dispatch('getDayProgram', itineraryId);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_add_day_program_entry_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_ADD_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_add_day_program_entry_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  deleteDayProgramEntry({ commit, state, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_DELETE_DAY_PENDING);
    return itineraryService.deleteDayProgramEntry(payload)
      .then(() => {
        const index = state.dayProgram.items.findIndex(day => day.id === payload.dayProgramId);
        commit(C.ITINERARY_DAY_PROGRAM_DELETE_DAY_SUCCESS, index);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_delete_day_program_entry_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_DELETE_DAY_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_delete_day_program_entry_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  updateDayProgram({ commit, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_UPDATE_PENDING);
    return itineraryService.updateDayProgram(payload)
      .then(() => {
        commit(C.ITINERARY_DAY_PROGRAM_UPDATE_SUCCESS, payload);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_update_day_program_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_UPDATE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_update_day_program_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setLocationDayProgram({ commit }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_LOCATION_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  updateDayProgramLocation({ commit, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_LOCATION_SAVE_PENDING);
    return itineraryService.updateDayProgramLocation(payload)
      .then(() => {
        dispatch('setDayProgramLocationDataOnUpdate', {
          data: payload.data,
        });
        commit(C.ITINERARY_DAY_PROGRAM_LOCATION_SAVE_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_update_day_program_location_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_LOCATION_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_update_day_program_location_error',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   */
  setDayProgramLocationDataOnUpdate({ commit, state }, payload) {
    const dayProgramList = [...state.dayProgram.items];
    const dayProgramIndex = dayProgramList.findIndex(
      dayprogram => dayprogram.id === payload.data.dayProgramId,
    );

    const newPayload = {
      dayProgramIndex,
      countryId: null,
      country: null,
      areaId: null,
      area: null,
    };

    if (payload.data.areaId) {
      const countriesWithAreas = [...state.masterCountries.items];
      const country = countriesWithAreas.filter(
        countryFilter => countryFilter.id === payload.data.countryId,
      );
      const area = country[0].areas.filter(areaFilter => areaFilter.id === payload.data.areaId);

      newPayload.countryId = country[0].id;
      newPayload.country = country[0].alias;
      newPayload.areaId = area[0].id;
      newPayload.area = area[0].name;
    }
    commit(C.ITINERARY_DAY_PROGRAM_LOCATION_UPDATE_DATA, newPayload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   */
  async fetchDayProgramHotelData({ commit, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_HOTEL_PENDING, payload.data);
    await dispatch('fetchHotelsByAreaId', { areaId: payload.data.areaId });
    await dispatch('fetchHotelsByDayProgramId', { itineraryId: payload.itineraryId, dayProgramId: payload.data.id });
    commit(C.ITINERARY_DAY_PROGRAM_HOTEL_CLEAR_LOADING);
  },

  /**
   * @param commit
   * @param areaId
   * @returns {PromiseLike<T> | Promise<T> | *}
   */
  fetchHotelsByAreaId({ commit }, { areaId }) {
    return twentyService.getCountriesByAreaIds([areaId]).then((res) => {
      commit(C.ITINERARY_DAY_PROGRAM_SET_AVAILABLE_HOTELS, res.data);
    });
  },

  /**
   * @param commit
   * @param itineraryId
   * @param dayProgramId
   * @returns {PromiseLike<T> | Promise<T> | *}
   */
  fetchHotelsByDayProgramId({ commit }, { itineraryId, dayProgramId }) {
    return itineraryService.getDayProgramHotels(itineraryId, dayProgramId).then((res) => {
      if (res.data) {
        commit(C.ITINERARY_DAY_PROGRAM_SET_SELECTED_HOTELS, res.data);
      }
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @param dayProgramId
   * @param siteIds
   * @returns {Promise<T>}
   */
  updateDayProgramHotels({ commit, state, dispatch }, { itineraryId, dayProgramId, siteIds }) {
    commit(C.ITINERARY_DAY_PROGRAM_HOTEL_SET_LOADING);
    return itineraryService.updateDayProgramHotels(itineraryId, dayProgramId, siteIds)
      .then(() => {
        const dayProgramList = [...state.dayProgram.items];
        const dayProgramIndex = dayProgramList.findIndex(
          dayprogram => dayprogram.id === dayProgramId,
        );
        commit(C.ITINERARY_DAY_PROGRAM_HOTEL_UPDATE_SUCCESS, {
          dayProgramIndex,
          hotelCount: siteIds.length,
        });
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_update_day_program_entry_hotels_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_HOTEL_CLEAR_LOADING);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_update_day_program_entry_hotels_error',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<void>}
   */
  async fetchDayProgramExcursionData({ commit }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_EXCURSIONS_PENDING, payload.data);

    return twentyService.getExcursionsByCountryId(payload.data.countryId).then((res) => {
      const mappedExcursions = res.data.map((e) => {
        const result = payload.data.extras.filter(s => s.id === e.id)[0] || null;
        // The backend has a universal response for extras, instead of messing with the response,
        // I simply set the type here.
        const excursion = { ...e, type: 'none' };
        return { ...excursion, ...result };
      });
      commit(C.ITINERARY_DAY_PROGRAM_SET_AVAILABLE_EXCURSIONS, mappedExcursions);
    }).catch(() => {
      commit(C.ITINERARY_DAY_PROGRAM_EXCURSIONS_CLEAR_LOADING);
    });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @param dayProgramId
   * @param mappings
   * @returns {Promise<T>}
   */
  updateDayProgramExcursions({ commit, state, dispatch }, { itineraryId, dayProgramId, extras }) {
    commit(C.ITINERARY_DAY_PROGRAM_EXCURSIONS_SET_LOADING);

    const filteredExtras = extras.filter(extra => extra.type === 'optional' || extra.type === 'included');
    const dayProgramList = cloneDeep(state.dayProgram.items);
    const dayProgramIndex = dayProgramList.findIndex(
      dayprogram => dayprogram.id === dayProgramId,
    );

    const mappedExtras = [...filteredExtras].map(extra => ({ extraId: extra.id, type: extra.type }));

    return itineraryService.updateDayProgramExcursions(itineraryId, dayProgramId, mappedExtras)
      .then(() => {
        commit(C.ITINERARY_DAY_PROGRAM_EXCURSIONS_UPDATE_SUCCESS, {
          dayProgramIndex,
          excursionCount: extras.length,
          extras: filteredExtras,
        });

        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_update_day_program_entry_excursions_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_EXCURSIONS_CLEAR_LOADING);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_update_day_program_entry_excursions_error',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   */
  getMealPlanTypes({ commit }) {
    commit(C.ITINERARY_MEAL_PLAN_TYPES_PENDING);
    itineraryService.getMealPlanTypes()
      .then((res) => {
        commit(C.ITINERARY_MEAL_PLAN_TYPES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_MEAL_PLAN_TYPES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  getDayProgramMealPlans({ commit, dispatch }, payload) {
    commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_PENDING, payload.data);
    return itineraryService.getDayProgramMealPlans(payload.itineraryId, payload.data.id)
      .then((res) => {
        commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_get_day_program_entry_meal_plans_error',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   * @param state
   * @param dispatch
   * @param itineraryId
   * @param dayProgramId
   * @param mappings
   * @returns {Promise<T>}
   */
  updateDayProgramMealPlans({ commit, state, dispatch }, { itineraryId, dayProgramId, mappings }) {
    commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_SET_LOADING);
    return itineraryService.updateDayProgramMealPlans(itineraryId, dayProgramId, mappings)
      .then(() => {
        const dayProgramList = [...state.dayProgram.items];
        const dayProgramIndex = dayProgramList.findIndex(
          dayprogram => dayprogram.id === dayProgramId,
        );
        commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_UPDATE_SUCCESS, {
          dayProgramIndex,
          mealplanCount: mappings.length,
        });

        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_update_day_program_entry_meal_plans_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_MEAL_PLANS_CLEAR_LOADING);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_update_day_program_entry_meal_plans_error',
        }, { root: true });
        throw new Error();
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T | never>}
   */
  getItineraryDomains({ commit }, itineraryId) {
    commit(C.ITINERARY_DOMAINS_PENDING);
    return itineraryService.getItineraryDomains(itineraryId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_DOMAINS_SUCCESS, res.data);
        } else {
          commit(C.ITINERARY_DOMAINS_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.ITINERARY_DOMAINS_FAILURE);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  selectItineraryDomain({ commit }, payload) {
    commit(C.ITINERARY_DOMAIN_SELECT, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  deselectItineraryDomain({ commit }) {
    commit(C.ITINERARY_DOMAIN_DESELECT);
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param domainIds
   */
  addItineraryDomains({ commit, dispatch }, { itineraryId, domainIds }) {
    commit(C.ITINERARY_DOMAINS_PENDING);
    const itineraryDomains = domainIds.map(domainId => ({
      itineraryId,
      domainId,
    }));
    return itineraryService.addItineraryDomains(itineraryId, itineraryDomains)
      .then(() => {
        dispatch('getItineraryDomains', itineraryId);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_domains_add_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DOMAINS_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_domains_add_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param domains
   * @param domainIds
   */
  updateItineraryDomains({ commit, dispatch }, { itineraryId, itineraryDomains }) {
    commit(C.ITINERARY_DOMAINS_PENDING);
    return itineraryService.updateItineraryDomains(itineraryId, itineraryDomains)
      .then(() => {
        dispatch('getItineraryDomains', itineraryId);
        commit(C.ITINERARY_DOMAINS_SUCCESS, itineraryDomains);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_domains_update_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_DOMAINS_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_domains_update_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param id
   * @returns {*}
   */
  getItineraryDayProgramImages({ commit, dispatch }, { dayProgramId }) {
    commit(C.ITINERARY_DAY_PROGRAM_IMAGES_PENDING);
    return filesService.getItineraryDayProgramImages(dayProgramId)
      .then((res) => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_day_program_images_fetch_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param images
   * @returns {Promise<T | never>}
   */
  uploadItineraryDayProgramImages({ commit, dispatch }, { dayProgramId, images }) {
    commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_PENDING);
    const formData = new FormData();
    images.forEach((image) => {
      formData.append('files[]', image);
    });
    return filesService.uploadItineraryDayProgramImages(dayProgramId, formData)
      .then((res) => {
        if (res.status === 204) {
          commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_day_program_images_save_success',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_day_program_images_fetch_error',
          }, { root: true });
        }
        dispatch('getItineraryDayProgramImages', { dayProgramId });
      })
      .catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_day_program_images_fetch_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param fileIds
   * @returns {Promise<T | never>}
   */
  updateItineraryDayProgramImages({ commit, dispatch }, { dayProgramId, fileIds }) {
    commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_PENDING);
    return filesService.updateImageOrder(fileIds)
      .then((res) => {
        if (res.status === 204) {
          commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_day_program_images_save_success',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_day_program_images_save_error',
          }, { root: true });
        }
        dispatch('getItineraryDayProgramImages', { dayProgramId });
      })
      .catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_day_program_images_save_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param image
   * @param imageId
   * @returns {Promise<T | never>}
   */
  deleteItineraryDayProgramImage({ commit, dispatch }, { dayProgramId, image }) {
    commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_PENDING);
    return filesService.deleteImage(image.id)
      .then((res) => {
        if (res.status === 204) {
          commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_day_program_images_delete_success',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_day_program_images_delete_error',
          }, { root: true });
        }
        dispatch('getItineraryDayProgramImages', { dayProgramId });
      })
      .catch(() => {
        commit(C.ITINERARY_DAY_PROGRAM_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_day_program_images_delete_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  updateSeoTeaserEntry({ commit, dispatch }, payload) {
    commit(C.ITINERARY_TEASER_UPDATE_PENDING);
    return itineraryService.updateSeoTeaserEntry(payload)
      .then(() => {
        commit(C.ITINERARY_TEASER_UPDATE_SUCCESS, payload);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_teaser_update_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_TEASER_UPDATE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_teaser_update_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T>}
   */
  getSeoTeaserEntries({ commit }, itineraryId) {
    commit(C.ITINERARY_TEASER_PENDING);
    return itineraryService.getSeoTeaserEntries(itineraryId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_TEASER_SUCCESS, res.data);
        } else {
          commit(C.ITINERARY_TEASER_SUCCESS, []);
        }
      }).catch(() => {
        commit(C.ITINERARY_TEASER_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  updateIncludeEntries({ commit, dispatch }, payload) {
    commit(C.ITINERARY_INCLUDE_UPDATE_PENDING);
    return itineraryService.updateIncludeEntries(payload, payload.includeNot)
      .then(() => {
        commit(C.ITINERARY_INCLUDE_UPDATE_SUCCESS, payload);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_include_update_success',
        }, { root: true });
      }).catch(() => {
        commit(C.ITINERARY_INCLUDE_UPDATE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_include_update_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param itineraryId
   * @returns {Promise<T>}
   */
  getIncludeEntries({ commit, dispatch }, payload) {
    commit(C.ITINERARY_INCLUDE_PENDING);
    return itineraryService.getIncludeEntries(payload.itineraryId, payload.includeNot)
      .then((res) => {
        const refactoredList = [];
        res.data.forEach((obj) => {
          const refactored = {};
          obj.domains.forEach((domain) => {
            refactored[`${domain.domainId}`] = domain;
          });
          refactoredList.push({
            includeCategoryId: obj.includeCategoryId,
            text: obj.text,
            headline: obj.headline,
            domains: refactored,
            priority: obj.priority,
          });
        });
        if (res.status === 200) {
          if (refactoredList.length) {
            commit(C.ITINERARY_INCLUDE_SUCCESS, refactoredList);
          } else {
            commit(C.ITINERARY_INCLUDE_NO_CONTENT);
            dispatch('snackbar/show', {
              identifier: 'warning',
              message: 'itinerary_include_no_content',
            }, { root: true });
          }
        } else {
          throw new Error();
        }
      }).catch(() => {
        commit(C.ITINERARY_INCLUDE_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T | never>}
   */
  getIncludeCategories({ commit }, itineraryId) {
    commit(C.ITINERARY_INCLUDE_CATEGORIES_PENDING);
    return itineraryService.getIncludeCategories(itineraryId)
      .then((res) => {
        commit(C.ITINERARY_INCLUDE_CATEGORIES_SUCCESS, res.data);
      }).catch(() => {
        commit(C.ITINERARY_INCLUDE_CATEGORIES_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param images
   * @returns {Promise<T | never>}
   */
  uploadBannerImage({ commit, dispatch }, { itineraryId, images }) {
    commit(C.ITINERARY_BANNER_IMAGES_SAVE_PENDING);
    const formData = new FormData();
    images.forEach((image) => {
      formData.append('files[]', image);
    });
    return filesService.uploadItineraryBannerImages(itineraryId, formData)
      .then((res) => {
        if (res.status === 204) {
          commit(C.ITINERARY_BANNER_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_banner_images_save_success',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_banner_images_save_error',
          }, { root: true });
        }
        dispatch('getItineraryBannerImages', itineraryId);
      })
      .catch(() => {
        commit(C.ITINERARY_BANNER_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_banner_images_save_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param fileIds
   * @returns {Promise<T | never>}
   */
  updateItineraryBannerImages({ commit, dispatch }, { itineraryId, fileIds }) {
    commit(C.ITINERARY_BANNER_IMAGES_SAVE_PENDING);
    return filesService.updateImageOrder(fileIds)
      .then((res) => {
        if (res.status === 204) {
          commit(C.ITINERARY_BANNER_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_banner_images_save_success',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_banner_images_save_error',
          }, { root: true });
        }
        dispatch('getItineraryBannerImages', itineraryId);
      })
      .catch(() => {
        commit(C.ITINERARY_BANNER_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_banner_images_save_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param image
   * @param imageId
   * @returns {Promise<T | never>}
   */
  deleteItineraryBannerImage({ commit, dispatch }, { itineraryId, image }) {
    commit(C.ITINERARY_BANNER_IMAGES_SAVE_PENDING);
    return filesService.deleteImage(image.id)
      .then((res) => {
        if (res.status === 204) {
          commit(C.ITINERARY_BANNER_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_banner_images_delete_success',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_banner_images_delete_error',
          }, { root: true });
        }
        dispatch('getItineraryBannerImages', itineraryId);
      })
      .catch(() => {
        commit(C.ITINERARY_BANNER_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_banner_images_delete_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @returns {Promise<T | never>}
   */
  getItineraryBannerImages({ commit, dispatch }, itineraryId) {
    commit(C.ITINERARY_BANNER_IMAGES_LOAD_PENDING);
    return filesService.getItineraryBannerImages(itineraryId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_BANNER_IMAGES_LOAD_SUCCESS, res.data);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_banner_images_load_success',
          }, { root: true });
        } else if (res.status === 204) {
          commit(C.ITINERARY_BANNER_IMAGES_LOAD_SUCCESS, []);
        } else {
          commit(C.ITINERARY_BANNER_IMAGES_LOAD_SUCCESS, []);
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_banner_images_load_error',
          }, { root: true });
        }
      })
      .catch(() => {
        commit(C.ITINERARY_BANNER_IMAGES_LOAD_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_banner_images_load_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param images
   * @returns {Promise<T | never>}
   */
  uploadMapImage({ commit, dispatch }, { itineraryId, images }) {
    commit(C.ITINERARY_MAP_IMAGES_SAVE_PENDING);
    const formData = new FormData();
    images.forEach((image) => {
      formData.append('files[]', image);
    });
    return filesService.uploadItineraryMapImages(itineraryId, formData)
      .then((res) => {
        if (res.status === 204) {
          commit(C.ITINERARY_MAP_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_map_images_save_success',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_map_images_save_error',
          }, { root: true });
        }
        dispatch('getItineraryMapImages', itineraryId);
      }).catch(() => {
        commit(C.ITINERARY_MAP_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_map_images_save_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @param fileIds
   * @returns {Promise<T | never>}
   */
  updateItineraryMapImages({ commit, dispatch }, { itineraryId, fileIds }) {
    commit(C.ITINERARY_MAP_IMAGES_SAVE_PENDING);
    return filesService.updateImageOrder(fileIds)
      .then((res) => {
        if (res.status === 204) {
          commit(C.ITINERARY_MAP_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_map_images_save_success',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_map_images_save_error',
          }, { root: true });
        }
        dispatch('getItineraryMapImages', itineraryId);
      }).catch(() => {
        commit(C.ITINERARY_MAP_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_map_images_save_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param dayProgramId
   * @param image
   * @param imageId
   * @returns {Promise<T | never>}
   */
  deleteItineraryMapImage({ commit, dispatch }, { itineraryId, image }) {
    commit(C.ITINERARY_MAP_IMAGES_SAVE_PENDING);
    return filesService.deleteImage(image.id)
      .then((res) => {
        if (res.status === 204) {
          commit(C.ITINERARY_MAP_IMAGES_SAVE_SUCCESS);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_map_images_delete_success',
          }, { root: true });
        } else {
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_map_images_delete_error',
          }, { root: true });
        }
        dispatch('getItineraryMapImages', itineraryId);
      })
      .catch(() => {
        commit(C.ITINERARY_MAP_IMAGES_SAVE_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_map_images_delete_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param itineraryId
   * @returns {Promise<T | never>}
   */
  getItineraryMapImages({ commit, dispatch }, itineraryId) {
    commit(C.ITINERARY_MAP_IMAGES_LOAD_PENDING);
    return filesService.getItineraryMapImages(itineraryId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_MAP_IMAGES_LOAD_SUCCESS, res.data);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_map_images_load_success',
          }, { root: true });
        } else if (res.status === 204) {
          commit(C.ITINERARY_MAP_IMAGES_LOAD_SUCCESS, []);
        } else {
          commit(C.ITINERARY_MAP_IMAGES_LOAD_SUCCESS, []);
          dispatch('snackbar/show', {
            identifier: 'error',
            message: 'itinerary_map_images_load_error',
          }, { root: true });
        }
      }).catch(() => {
        commit(C.ITINERARY_MAP_IMAGES_LOAD_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_map_images_load_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T | never>}
   */
  syncItineraryPriceIntervals({ commit, dispatch }, payload) {
    const { domainId, itineraryId, priceIntervals } = payload;
    commit(C.ITINERARY_PRICE_INTERVAL_SYNC_PENDING);
    return itineraryService.syncItineraryPriceIntervals(itineraryId, domainId, priceIntervals)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_PRICE_INTERVAL_SYNC_SUCCESS, res.data);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_price_intervals_sync_success',
          }, { root: true });
        }
      }).catch(() => {
        commit(C.ITINERARY_PRICE_INTERVAL_SYNC_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_price_intervals_sync_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T | never>}
   */
  getItineraryPriceIntervals({ commit }, payload) {
    commit(C.ITINERARY_PRICE_INTERVALS_GET_PENDING);
    const { itineraryId, domainId } = payload;
    return itineraryService.getItineraryPriceIntervals(itineraryId, domainId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_PRICE_INTERVALS_GET_SUCCESS, res.data);
        } else if (res.status === 204) {
          commit(C.ITINERARY_PRICE_INTERVALS_GET_NO_CONTENT);
        }
      }).catch(() => {
        commit(C.ITINERARY_PRICE_INTERVALS_GET_FAILURE);
      });
  },

  getItineraryDayProgram({ commit }, itineraryId) {
    commit(C.ITINERARY_DAY_PROGRAM_PRICES_PENDING);
    itineraryService.getDayProgram(itineraryId).then((res) => {
      commit(C.ITINERARY_DAY_PROGRAM_PRICES_SUCCESS, res.data);
    }).catch(() => {
      commit(C.ITINERARY_DAY_PROGRAM_PRICES_FAILURE);
    });
  },

  /**
   * @param commit
   * @param payload
   * @param priceIntervalId
   * @param dispatch
   * @returns {Promise<T | never>}
   */
  syncItineraryPriceSettings({ commit, dispatch }, payload) {
    commit(C.ITINERARY_PRICE_SETTINGS_SYNC_PENDING);
    return itineraryService.syncItineraryPriceSettings(payload).then((res) => {
      if (res.status === 200) {
        commit(C.ITINERARY_PRICE_SETTINGS_SYNC_SUCCESS, res.data);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_price_setting_sync_success',
        }, { root: true });
      } else {
        throw new Error();
      }
    }).catch(() => {
      commit(C.ITINERARY_PRICE_SETTINGS_SYNC_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'itinerary_price_setting_sync_error',
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param dispatch
   * @param priceIntervalId
   * @returns {Promise<T | never>}
   */
  getItineraryPriceSettings({ commit, dispatch }, priceIntervalId) {
    commit(C.ITINERARY_PRICE_SETTINGS_GET_PENDING);
    return itineraryService.getItineraryPriceSettings(priceIntervalId)
      .then((res) => {
        if (res.status === 200) {
          commit(C.ITINERARY_PRICE_SETTINGS_GET_SUCCESS, res.data);
          dispatch('snackbar/show', {
            identifier: 'success',
            message: 'itinerary_price_setting_get_success',
          }, { root: true });
        } else if (res.status === 204) {
          commit(C.ITINERARY_PRICE_SETTINGS_GET_NO_CONTENT);
          dispatch('snackbar/show', {
            identifier: 'warning',
            message: 'itinerary_price_setting_get_no_content',
          }, { root: true });
        }
      }).catch(() => {
        commit(C.ITINERARY_PRICE_SETTINGS_GET_FAILURE);
        dispatch('snackbar/show', {
          identifier: 'error',
          message: 'itinerary_price_setting_get_error',
        }, { root: true });
      });
  },

  /**
   * @param commit
   * @param payload
   * @param priceIntervalId
   * @param dispatch
   * @returns {Promise<T | never>}
   */
  copyItineraryPriceSettings({ commit, dispatch }, payload) {
    commit(C.ITINERARY_PRICE_SETTINGS_COPY_PENDING);
    return itineraryService.copyItineraryPriceSettings(payload.priceIntervalId).then((res) => {
      if (res.status === 200) {
        commit(C.ITINERARY_PRICE_SETTINGS_COPY_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_price_setting_copy_success',
        }, { root: true });
        dispatch('getItineraryPriceIntervals', payload);
      } else {
        throw new Error();
      }
    }).catch(() => {
      commit(C.ITINERARY_PRICE_SETTINGS_COPY_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'itinerary_price_setting_copy_error',
      }, { root: true });
    });
  },

  /**
   * @param commit
   * @param payload
   * @param priceIntervalId
   * @param dispatch
   * @returns {Promise<T | never>}
   */
  cloneItinerary({ commit, dispatch }, payload) {
    commit(C.ITINERARY_CLONE_PENDING);
    return itineraryService.cloneItinerary(payload.itineraryId).then((res) => {
      if (res.status === 200) {
        commit(C.ITINERARY_CLONE_SUCCESS);
        dispatch('snackbar/show', {
          identifier: 'success',
          message: 'itinerary_clone_success',
        }, { root: true });
        location.href = `/itinerary/edit/${res.data.id}/general`;
      } else {
        throw new Error();
      }
    }).catch(() => {
      commit(C.ITINERARY_CLONE_FAILURE);
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'itinerary_clone_error',
      }, { root: true });
    });
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/itinerary/actions.js